import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import * as api from "./api";
import type { AdvisoryResponse } from "./types";

/** Kto widzi graf procesu (i dane, które tylko z niego płyną: fazy, prognoza,
 *  historia stanu): CEO / dyrektor / każdy koordynator DLC_01 / osoba z grantem
 *  „Doradca AI (ZE)". Lustro serwerowego CanAccessZEAdvisor — trzymamy je w jednym
 *  miejscu, bo pyta o to i zakładka Doradcy, i modal szczegółów kamienia. */
export function useCanSeeProcessGraph(): boolean {
  const user = useAuthStore((s) => s.user);
  return (
    !!user &&
    (user.is_ceo ||
      user.role === "director" ||
      user.role === "coordinator" ||
      !!user.has_ze_advisor_access)
  );
}

const qk = {
  advisory: (id: string) => ["pp", "advisory", id] as const,
  snapshot: (id: string) => ["pp", "advisory-snapshot", id] as const,
};

const RUNNING = new Set(["running", "pending"]);

export function useAdvisory(connectionId: string | undefined) {
  return useQuery<AdvisoryResponse>({
    queryKey: qk.advisory(connectionId ?? ""),
    queryFn: () => api.getAdvisory(connectionId as string),
    enabled: !!connectionId,
    refetchInterval: (q) =>
      q.state.data?.run && RUNNING.has(q.state.data.run.status) ? 3000 : false,
  });
}

export function useAdvisorySnapshot(connectionId: string | undefined, enabled: boolean) {
  return useQuery<Record<string, unknown>>({
    queryKey: qk.snapshot(connectionId ?? ""),
    queryFn: () => api.getAdvisorySnapshot(connectionId as string),
    enabled: enabled && !!connectionId,
    staleTime: 30_000,
  });
}

export function useMarkAdvisorySeen(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markAdvisorySeen(connectionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function useRecomputeAdvisory(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.recomputeAdvisory(connectionId),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }), 1500);
    },
  });
}

export function useCreateAdvisorNote(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { description: string; files: File[] }) =>
      api.createAdvisorNote(connectionId, v.description, v.files),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function usePutDirectives(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.putDirectives(connectionId, text),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function useSuggestionFeedback(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: "done" | "dismissed" }) =>
      api.suggestionFeedback(v.id, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function useSuggestionComment(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: string }) => api.suggestionComment(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function useOversight() {
  return useQuery({
    queryKey: ["pp", "advisory-oversight"] as const,
    queryFn: () => api.getOversight(),
  });
}

export function useAnalytics(params: Record<string, string | string[]>) {
  return useQuery({
    queryKey: ["pp", "advisory-analytics", params] as const,
    queryFn: () => api.getAnalytics(params),
    staleTime: 30_000,
  });
}

export function useApplySuggestion(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.applySuggestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function usePromoteSuggestion(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.promoteSuggestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.advisory(connectionId) }),
  });
}

export function useProjectCompare(ids: string[]) {
  return useQuery({
    queryKey: ["pp", "advisory-compare", ids] as const,
    queryFn: () => api.getProjectCompare(ids),
    enabled: ids.length >= 2,
  });
}

export function useCoordinators(params: Record<string, string | string[]>) {
  return useQuery({
    queryKey: ["pp", "advisory-coordinators", params] as const,
    queryFn: () => api.getCoordinators(params),
    staleTime: 30_000,
  });
}

export function useMeta(params: Record<string, string>) {
  return useQuery({
    queryKey: ["pp", "advisory-meta", params] as const,
    queryFn: () => api.getMeta(params),
    staleTime: 30_000,
  });
}

export function useControlBoard(params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ["pp", "control-board", params] as const,
    queryFn: () => api.getControlBoard(params),
    staleTime: 30_000,
  });
}

export function useSlaRules(params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ["pp", "sla-rules", params] as const,
    queryFn: () => api.listSlaRules(params),
    staleTime: 30_000,
  });
}

export function useSlaMutation(invalidateParams: Record<string, string> = {}) {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["pp", "sla-rules", invalidateParams] });
    qc.invalidateQueries({ queryKey: ["pp", "location-clock-config"] });
  };
  return {
    create: useMutation({ mutationFn: (b: Partial<import("./types").SlaRule>) => api.createSlaRule(b), onSuccess: inval }),
    update: useMutation({
      mutationFn: ({ id, ...b }: Partial<import("./types").SlaRule> & { id: string }) =>
        api.patchSlaRule(id, b),
      onSuccess: inval,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteSlaRule(id), onSuccess: inval }),
  };
}

// ── zegary lokalizacji: konfiguracja per kategoria + szablony torów ─────────────
export function useLocationClockConfig() {
  return useQuery({
    queryKey: ["pp", "location-clock-config"] as const,
    queryFn: () => api.getLocationClockConfig(),
    staleTime: 30_000,
  });
}

export function useLocationTrackTemplateMutation() {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["pp", "location-clock-config"] });
  return {
    save: useMutation({
      mutationFn: ({ category, kinds }: { category: string; kinds: string[] }) =>
        api.putLocationTrackTemplate(category, kinds),
      onSuccess: inval,
    }),
    reset: useMutation({
      mutationFn: (category: string) => api.resetLocationTracks(category),
      onSuccess: inval,
    }),
  };
}

// ── tor lokalizacji działki ─────────────────────────────────────────────────────
export function useParcelTrack(parcelId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "parcel-track", parcelId ?? ""] as const,
    queryFn: () => api.getParcelTrack(parcelId as string),
    enabled: !!parcelId,
  });
}

export function useParcelTrackMutation(parcelId: string | undefined) {
  const qc = useQueryClient();
  const refresh = (track: import("./types").ParcelTrack) => {
    qc.setQueryData(["pp", "parcel-track", parcelId ?? ""], track);
    qc.invalidateQueries({ queryKey: ["pp", "process-graph"] });
    qc.invalidateQueries({ queryKey: ["pp", "control"] });
  };
  return {
    add: useMutation({
      mutationFn: (body: Record<string, unknown>) =>
        api.addParcelTrackElement(parcelId as string, body),
      onSuccess: refresh,
    }),
    patch: useMutation({
      mutationFn: ({ elementId, ...body }: Record<string, unknown> & { elementId: string }) =>
        api.patchParcelTrackElement(parcelId as string, elementId, body),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (elementId: string) =>
        api.deleteParcelTrackElement(parcelId as string, elementId),
      onSuccess: refresh,
    }),
    reset: useMutation({
      mutationFn: () => api.resetParcelTrack(parcelId as string),
      onSuccess: refresh,
    }),
  };
}

// ── rykoszet przeprojektowania ──────────────────────────────────────────────────
export function useRouteImpact(connectionId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "route-impact", connectionId ?? ""] as const,
    queryFn: () => api.getRouteImpact(connectionId as string),
    enabled: !!connectionId,
    staleTime: 30_000,
  });
}

export function useRouteImpactMutation(connectionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ revert, keep }: { revert: string[]; keep: string[] }) =>
      api.applyRouteImpact(connectionId as string, revert, keep),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "route-impact", connectionId ?? ""] });
      qc.invalidateQueries({ queryKey: ["pp", "process-graph"] });
      qc.invalidateQueries({ queryKey: ["pp"] });
    },
  });
}

export function useControl(connectionId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "control", connectionId ?? ""] as const,
    queryFn: () => api.getControl(connectionId as string),
    enabled: !!connectionId,
    staleTime: 30_000,
  });
}

export function useProcessGraph(connectionId: string | undefined, expandLocation = false) {
  return useQuery({
    queryKey: ["pp", "process-graph", connectionId ?? "", expandLocation] as const,
    queryFn: () => api.getProcessGraph(connectionId as string, expandLocation),
    enabled: !!connectionId,
  });
}

export function useParcelGraph(connectionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["pp", "parcel-graph", connectionId ?? ""] as const,
    queryFn: () => api.getParcelGraph(connectionId as string),
    enabled: enabled && !!connectionId,
  });
}

export function useGraphEdge(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { node: string; depends_on: string; remove?: boolean }) =>
      v.remove ? api.removeGraphEdge(connectionId, v.node, v.depends_on)
               : api.addGraphEdge(connectionId, v.node, v.depends_on),
    // key now carries expandLocation → invalidate the prefix (refetches active view).
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pp", "process-graph", connectionId] }),
  });
}

export function useGraphNodeState(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { node: string; state: string; reason: string }) =>
      api.setGraphNodeState(connectionId, v.node, v.state, v.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "process-graph", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "control", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "todo", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "graph-proposals", connectionId] });
      // Graf i tablica kamieni to te same kroki — odśwież kamienie + kafelki nagłówka,
      // żeby zmiana stanu (np. „nie dotyczy") była widoczna od razu, bez F5.
      qc.invalidateQueries({ queryKey: ["pp", "connection", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "by-project"] });
      // Zakładka „Wody Polskie" pokazuje te same propozycje co graf i tablica kamieni —
      // po zastosowaniu jednej z nich nie może zostać z listą sprzed zmiany.
      qc.invalidateQueries({ queryKey: ["pp", "wody", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "attention", connectionId] });
    },
  });
}

// Zapis ręcznej pozycji kafelka: NIE unieważnia grafu — pozycja żyje lokalnie
// w React Flow, więc brak migotania / przeliczania układu przy każdym przesunięciu.
export function useGraphNodePosition(connectionId: string) {
  return useMutation({
    mutationFn: (v: { node: string; x: number; y: number }) =>
      api.setGraphNodePosition(connectionId, v.node, v.x, v.y),
  });
}

// Reset do auto-układu dagre: usuwa zapisane pozycje i odświeża graf.
export function useClearGraphPositions(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.clearGraphPositions(connectionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pp", "process-graph", connectionId] }),
  });
}

export function useGraphNodePhases(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { node: string; submit_label?: string; resolve_label?: string; submit_sla_days?: number | null; resolve_sla_days?: number | null }) =>
      api.setGraphNodePhases(connectionId, v.node, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "process-graph", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "control", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "todo", connectionId] });
    },
  });
}

export function useGraphProposals(connectionId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "graph-proposals", connectionId ?? ""] as const,
    queryFn: () => api.getGraphProposals(connectionId as string),
    enabled: !!connectionId,
    staleTime: 30_000,
  });
}

export function useApplyTrackProposal(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { parcel_id: string; element_id: string | null; element_kind: string; action: string }) =>
      api.applyTrackProposal(connectionId, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "process-graph", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "graph-proposals", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "control", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "todo", connectionId] });
      // tor działki widoczny też w modalu — odśwież payload toru
      qc.invalidateQueries({ queryKey: ["pp", "parcel-track"] });
    },
  });
}

export function useTemplateCalibration(templateId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "template-calibration", templateId ?? ""] as const,
    queryFn: () => api.getTemplateCalibration(templateId as string),
    enabled: !!templateId,
  });
}

export function useProjectTodo(connectionId: string | undefined) {
  return useQuery({
    queryKey: ["pp", "todo", connectionId ?? ""] as const,
    queryFn: () => api.getProjectTodo(connectionId as string),
    enabled: !!connectionId,
    staleTime: 30_000,
  });
}

export function useGlobalTodoFilters() {
  return useQuery({
    queryKey: ["pp", "todo-global-filters"] as const,
    queryFn: () => api.getGlobalTodoFilters(),
  });
}

export function useGlobalTodo(
  filters?: { projects?: string[]; designers?: string[] },
  enabled = true,
) {
  return useQuery({
    queryKey: ["pp", "todo-global",
      [...(filters?.projects ?? [])].sort().join(","),
      [...(filters?.designers ?? [])].sort().join(","),
    ] as const,
    queryFn: () => api.getGlobalTodo(filters),
    enabled,
    staleTime: 30_000,
  });
}

// ── własne zadania „Moje TODO" ─────────────────────────────────────────────────
export function useMyTasks() {
  return useQuery({
    queryKey: ["pp", "my-tasks"] as const,
    queryFn: () => api.listMyTasks(),
    staleTime: 30_000,
  });
}

export function useMyTaskMutation() {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["pp", "my-tasks"] });
  return {
    create: useMutation({
      mutationFn: (b: import("./types").PersonalTaskInput) => api.createMyTask(b),
      onSuccess: inval,
    }),
    update: useMutation({
      mutationFn: ({ id, ...b }: Partial<import("./types").PersonalTaskInput> & { id: string }) =>
        api.patchMyTask(id, b),
      onSuccess: inval,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteMyTask(id),
      onSuccess: inval,
    }),
  };
}

export function useResetProcessGraph(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { templateId?: string }) => api.resetProcessGraph(connectionId, v.templateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pp", "process-graph", connectionId] });
      qc.invalidateQueries({ queryKey: ["pp", "control", connectionId] });
      // reset przebudowuje też kamienie projektu (tablica kamieni, nagłówki)
      qc.invalidateQueries({ queryKey: ["pp"] });
    },
  });
}

export function useProcessTemplates(enabled = true) {
  return useQuery({
    queryKey: ["pp", "process-templates"] as const,
    queryFn: () => api.listProcessTemplates(),
    enabled,
    staleTime: 30_000,
  });
}

export function useProcessTemplateMutation() {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["pp", "process-templates"] });
  return {
    save: useMutation({
      mutationFn: (t: import("./types").ProcessTemplate) => api.saveProcessTemplate(t),
      onSuccess: inval,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteProcessTemplate(id),
      onSuccess: inval,
    }),
    apply: useMutation({
      mutationFn: ({ id, connections, reset }: { id: string; connections: string[]; reset?: boolean }) =>
        api.applyProcessTemplate(id, connections, reset),
      // propagacja przebudowuje kamienie/grafy wybranych projektów
      onSuccess: () => qc.invalidateQueries({ queryKey: ["pp"] }),
    }),
    preview: useMutation({
      mutationFn: ({ id, connections, reset }: { id: string; connections: string[]; reset?: boolean }) =>
        api.dryRunProcessTemplate(id, connections, reset),
    }),
  };
}

export function useTemplateTargets(templateId?: string, enabled = true) {
  return useQuery({
    queryKey: ["pp", "process-template-targets", templateId ?? ""] as const,
    queryFn: () => api.listTemplateTargets(templateId),
    enabled,
    staleTime: 30_000,
  });
}
